import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Phone, MapPin } from "lucide-react";
import { heroSlides, company } from "../mock";

const HeroSlider = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = heroSlides.length;

  const go = useCallback((dir) => setIndex((i) => (i + dir + total) % total), [total]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => go(1), 6000);
    return () => clearInterval(t);
  }, [go, paused]);

  return (
    <section
      className="hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-slider"
    >
      <div className="hero__frame">
        {heroSlides.map((s, i) => (
          <div key={s.id} className={`hero__slide ${i === index ? "is-active" : ""}`} aria-hidden={i !== index}>
            <img src={s.image} alt={s.title} className="hero__img" loading={i === 0 ? "eager" : "lazy"} />
            <div className="hero__overlay" />
            {/* Artistic colour shards similar to the reference banner */}
            <div className="hero__shards" aria-hidden="true">
              <span className="shard shard--1" />
              <span className="shard shard--2" />
              <span className="shard shard--3" />
              <span className="shard shard--4" />
              <span className="shard shard--5" />
              <span className="shard shard--6" />
            </div>

            <div className="hero__card">
              <p className="hero__eyebrow">{s.eyebrow}</p>
              <h1 className="hero__title">{s.title}</h1>
              <p className="hero__subtitle">{s.subtitle}</p>
              <p className="hero__text">{s.text}</p>
              <div className="flex flex-wrap items-center gap-4 mt-6">
                <Link to={s.cta.path} className="btn-green btn-green--lg" data-testid={`hero-cta-${s.id}`}>
                  {s.cta.label}
                </Link>
                <a href={`tel:${company.phone}`} className="flex items-center gap-2 text-[#231f20] font-semibold text-[15px] hover:text-[#7ab929] transition-colors">
                  <Phone size={16} className="text-[#7ab929]" /> {company.phoneDisplay}
                </a>
                <a href={`tel:${company.phone2}`} className="text-[#231f20] font-semibold text-[15px] hover:text-[#7ab929] transition-colors">
                  / {company.phone2Display}
                </a>
              </div>
              {i === 0 && (
                <p className="hero__address">
                  <MapPin size={14} className="text-[#7ab929] shrink-0 mt-[3px]" />
                  <span>{company.address}</span>
                </p>
              )}
            </div>
          </div>
        ))}

        <button type="button" className="hero__arrow hero__arrow--prev" onClick={() => go(-1)} aria-label="Previous slide" data-testid="hero-prev">
          <ChevronLeft size={28} />
        </button>
        <button type="button" className="hero__arrow hero__arrow--next" onClick={() => go(1)} aria-label="Next slide" data-testid="hero-next">
          <ChevronRight size={28} />
        </button>
      </div>

      <div className="hero__dots" role="tablist">
        {heroSlides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Go to slide ${i + 1}`}
            className={`hero__dot ${i === index ? "is-active" : ""}`}
            onClick={() => setIndex(i)}
            data-testid={`hero-dot-${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
